import { inject, Injectable, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { EMPTY, interval, Observable, of } from "rxjs";
import { catchError, exhaustMap, filter, finalize, map, switchMap, tap } from "rxjs/operators";
import {
  ADVANCED_ACCOUNT_SETUP_STATUS_PROVIDER,
  ADVANCED_ACCOUNT_STATUS_CHECK_INTERVAL_MS,
  AdvancedAccountSetupTarget,
  AdvancedAccountStatus
} from "../types/advanced-account-setup.types";

@Injectable()
export class AdvancedAccountSetupService {
  private readonly statusProvider = inject(ADVANCED_ACCOUNT_SETUP_STATUS_PROVIDER);

  readonly status = signal<AdvancedAccountStatus>(AdvancedAccountStatus.Pending);
  readonly retrying = signal<boolean>(false);

  private readonly status$ = toObservable(this.status);

  init(status: AdvancedAccountStatus): void {
    this.status.set(status);
  }

  watchStatus(target: AdvancedAccountSetupTarget): Observable<AdvancedAccountStatus> {
    return this.status$.pipe(
      switchMap((status) => (status === AdvancedAccountStatus.Pending ? interval(ADVANCED_ACCOUNT_STATUS_CHECK_INTERVAL_MS) : EMPTY)),
      exhaustMap(() => this.statusProvider.getStatus(target).pipe(catchError(() => EMPTY))),
      filter((status) => !!status && status !== this.status()),
      tap((status) => this.status.set(status))
    );
  }

  retry(target: AdvancedAccountSetupTarget): Observable<boolean> {
    if (this.retrying()) {
      return EMPTY;
    }

    this.retrying.set(true);

    return this.statusProvider.retry(target).pipe(
      map(() => true),
      tap(() => this.status.set(AdvancedAccountStatus.Pending)),
      catchError(() => {
        this.status.set(AdvancedAccountStatus.Error);
        return of(false);
      }),
      finalize(() => this.retrying.set(false))
    );
  }
}
